import {
  Button,
  Center,
  Container,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { redirect } from '@remix-run/node'
import { Form, Link, data, useActionData } from '@remix-run/react'
import { useState } from 'react'
import { z } from 'zod'
import { getUserFormAuthenticator } from '~/authenticator.server'
import { LoginForm, loginFormSchema } from '~/components/login-form'
import { VALIDATION_MESSAGES } from '~/core/shared/messages'
import { ERROR_MESSAGES } from '~/core/shared/messages'
import { isErrorWithCode } from '~/core/shared/utils/error'
import {
  generateMetaDescriptor,
  passwordValidationRegex,
} from '~/core/shared/utils/misc'
import { commitSession, getSession } from '~/session.server'

export const meta: MetaFunction<typeof loader> = (metaInfo) =>
  generateMetaDescriptor(metaInfo, {
    pageTitle: 'ログイン',
  })

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get('cookie'))
  const user = session.get('user')
  if (user) {
    // ユーザーあればmypageに飛ばす
    return redirect('/mypage')
  }

  return null
}

export async function action({ request }: ActionFunctionArgs) {
  try {
    const authenticator = getUserFormAuthenticator()
    const user = await authenticator.authenticate('user', request)
    const session = await getSession(request.headers.get('cookie'))
    session.set('user', user)

    // もしユーザーに既にlineIdがあれば、セッションにline情報をセットする
    if (user.lineId) {
      session.set('line', { lineId: user.lineId })
    }
    return redirect('/mypage', {
      headers: { 'Set-Cookie': await commitSession(session) },
    })
  } catch (error) {
    const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
    return data({
      applicationError: ERROR_MESSAGES[errorCode],
    })
  }
}

export const handle = {
  breadcrumb: () => {
    return [
      {
        id: 'login',
        title: 'ログイン',
        href: '/login',
      },
      {
        id: 'userpass',
        title: 'メールアドレス・パスワードでログイン',
      },
    ]
  },
}

export default function LoginUserPass() {
  const actionData = useActionData<typeof action>()

  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string[]
  }>({})

  // actionでrequestを読んでvalidationするとrequestのbodyUsedがtrueになってしまい、
  // remix-auth-form-strategyで認証エラーになる（requestのbodyを再利用できない）
  // そのため、ログインフォームをフロントエンドでバリデーションする
  const isString = (value: unknown): value is string => {
    return typeof value === 'string'
  }

  const validate = () => {
    const formData = new FormData(document.querySelector('form') ?? undefined)
    const emailField = formData.get('email') // type FormDataEntryValue = File | string;
    const passwordField = formData.get('password')

    const email = isString(emailField) ? emailField : null
    const password = isString(passwordField) ? passwordField : null

    const validationResult = loginFormSchema.safeParse({ email, password })

    if (!validationResult.success) {
      setValidationErrors(validationResult.error.flatten().fieldErrors)
      return false
    }

    setValidationErrors({})
    return true
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (validate()) {
      ;(event.currentTarget as HTMLFormElement).submit()
    }
  }

  return (
    <Container mb='xl' pb='xl' size='md'>
      <Form method='POST' onSubmit={handleSubmit}>
        <Title my='lg' order={3}>
          ログイン
        </Title>
        <LoginForm
          validationErrors={validationErrors}
          actionData={actionData}
        />
      </Form>
    </Container>
  )
}
